// The PATIENT's side of a doctor↔patient thread inside the tenant app.
//
// Design note: the thread now renders through ChatMessageBubble / ChatComposer /
// ChatImageLightbox, the same three components PatientChatInterface uses and a
// match for the MOC patient web app's components/chat/*. That is deliberate —
// "both sides look the same" is only durable if there is ONE implementation of
// the bubble and the composer rather than two files kept in step by hand.
//
// The repeating illustration background that used to sit behind the thread has
// been removed: it fought with the bubbles and existed on no other surface in
// the product.
//
// Transport is unchanged and socket-first (Backend/socket/socketServer.js);
// POST /api/chat/send is the one REST route, used for anything carrying a file.
// It persists the row AND broadcasts receiveMessage to the room, so an upload
// must not also be appended optimistically here.
//
// REFUSALS STAY IN THE THREAD (P26). A socket `error` for this thread is read
// here (chatShared#readChatSocketError): a refused send takes its bubble down
// and puts the text back in the box, and READ_ONLY_PROFILE locks the composer
// with the reason. A profile the page already knows is read-only (`readOnly`)
// starts locked, so nobody has to be refused to find out.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ChevronDown, Loader2, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import baseURL from '../../utils/baseURL';
import ChatComposer from './ChatComposer';
import ChatImageLightbox from './ChatImageLightbox';
import ChatMessageBubble from './ChatMessageBubble';
import {
    MAX_CHAT_FILES,
    applyReactionLocally,
    downloadChatAttachment,
    pickValidChatFiles,
    readChatSocketError,
    serverMessageId,
} from './chatShared';
import { formatDateHeader } from '../../utils/dateHeader';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import getFileURL from '../../utils/fileURL';

const MAX_MESSAGE_LENGTH = 1000;

const formatMessage = (msg) => ({
    ...msg,
    id: msg.messageId || msg._id || msg.id,
    timestamp: msg.createdAt ? new Date(msg.createdAt) : null,
    type: msg.senderRole === 'patient' ? 'user' : 'doctor',
    content: msg.message,
    isRead: msg.isRead || false,
    attachments: msg.attachments || [],
    reactions: Array.isArray(msg.reactions) ? msg.reactions : [],
});

const DoctorChatInterface = ({ socket, doctor, patientId, readOnly = false }) => {
    const { t, i18n } = useTranslation(['chat']);
    const [messages, setMessages] = useState([]);
    // The server's READ_ONLY_PROFILE answer, when it arrives before (or
    // without) the page knowing the profile is read-only.
    const [refusedReadOnly, setRefusedReadOnly] = useState('');
    const [input, setInput] = useState('');
    const [inputError, setInputError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [isOnline, setIsOnline] = useState(false);
    const [roomId, setRoomId] = useState(null);
    const [replyToMessage, setReplyToMessage] = useState(null);
    const [showScrollBottom, setShowScrollBottom] = useState(false);

    // File upload state
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [previewImages, setPreviewImages] = useState([]);
    const [previewImageIndex, setPreviewImageIndex] = useState(0);

    const messagesEndRef = useRef(null);
    const currentChatRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const chatAreaRef = useRef(null);
    // Auto-scroll only when the reader is already at the bottom — otherwise a
    // new message yanks them away from the older one they were reading.
    const atBottomRef = useRef(true);
    // The latest rows, for the socket error handler registered once per thread.
    const messagesRef = useRef(messages);
    useEffect(() => { messagesRef.current = messages; }, [messages]);

    const lockedReason = refusedReadOnly || (readOnly
        ? t('chat.read_only_composer', 'You have read-only access to this profile. You can read the conversation but not write in it.')
        : '');

    // Load chat history when doctor changes
    useEffect(() => {
        if (!doctor || !doctor.doctorId || !socket) return;

        // Reset state only when actual chat target changes
        const chatKey = `${doctor.doctorId}-${patientId}`;
        if (currentChatRef.current !== chatKey) {
            setMessages([]);
            currentChatRef.current = chatKey;
        }
        setRoomId(null);
        setIsLoading(true);
        setIsTyping(false);
        setIsOnline(false);
        // Staged files and a half-written reply belong to the thread being left.
        setSelectedFiles([]);
        setReplyToMessage(null);
        setInputError('');
        setShowScrollBottom(false);
        atBottomRef.current = true;

        socket.emit('initChat', { doctorId: doctor.doctorId, patientId });

        const handleChatInitialized = (data) => {
            const newRoomId = data.roomId;
            setRoomId(newRoomId);
            socket.emit('joinRoom', { roomId: newRoomId, userId: patientId, role: 'patient' });
            socket.emit('fetchHistory', { roomId: newRoomId, page: 1, limit: 50 });
        };

        const handleRoomSummary = (summary) => {
            if (summary.roomId) setRoomId(summary.roomId);
            if (summary.doctor) setIsOnline(summary.doctor.isOnline || false);
        };

        const handleHistory = (data) => {
            const formattedMessages = (data.messages || []).map(formatMessage);
            setMessages(prev => {
                const merged = [...formattedMessages];
                // Keep anything optimistic that history doesn't know about yet.
                prev.forEach(m => {
                    const id = m.id || m._id;
                    if (!formattedMessages.find(fm => (fm.id || fm._id) === id)) merged.push(m);
                });
                return merged.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            });
            setIsLoading(false);
        };

        const handleReceiveMessage = (msg) => {
            setMessages(prev => {
                const formattedMsg = formatMessage(msg);

                // Reconcile the echo of our own optimistic message with the
                // server id/timestamp. Uploads never have one (the REST route
                // is the only writer), hence the attachment check.
                if (msg.senderId === patientId && msg.senderRole === 'patient' && (!msg.attachments || msg.attachments.length === 0)) {
                    const optimisticIndex = prev.findIndex(m => m.pending && m.type === 'user' && m.content === msg.message);
                    if (optimisticIndex !== -1) {
                        const updated = [...prev];
                        updated[optimisticIndex] = formattedMsg;
                        return updated;
                    }
                }

                if (prev.some(m => !m.pending && String(m.id) === String(formattedMsg.id))) return prev;
                return [...prev, formattedMsg];
            });
        };

        const handleUserTyping = (data) => {
            if (data.role === 'doctor' && data.userId === doctor.doctorId) setIsTyping(true);
        };
        const handleUserStoppedTyping = (data) => {
            if (data.role === 'doctor' && data.userId === doctor.doctorId) setIsTyping(false);
        };
        const handleUserStatusUpdate = (data) => {
            if (data.role === 'doctor' && data.userId === doctor.doctorId) setIsOnline(data.isOnline || false);
        };
        const handleMessagesSeen = (data) => {
            if (data.role === 'doctor') {
                setMessages(prev => prev.map(msg => (msg.type === 'user' ? { ...msg, isRead: true, status: 'read' } : msg)));
            }
        };
        // The server broadcasts the FULL new array, so this is a straight
        // replace — no merging, and a client that missed an earlier reaction
        // while reconnecting still lands on the truth. A message id this thread
        // doesn't hold simply matches nothing, which is the filter.
        const handleMessageReaction = (data) => {
            if (!data?.messageId) return;
            setMessages(prev => prev.map(msg => (String(msg.id) === String(data.messageId)
                ? { ...msg, reactions: Array.isArray(data.reactions) ? data.reactions : [] }
                : msg)));
        };
        const handleReconnect = () => {
            socket.emit('initChat', { doctorId: doctor.doctorId, patientId });
        };
        // A refusal is shown HERE, in the thread — never as a page-wide error.
        const handleSocketError = (payload) => {
            const refusal = readChatSocketError(payload, t('chat.sendFailed', 'Failed to send message'));
            if (!refusal) return;
            if (refusal.sendRefused) {
                const pending = messagesRef.current.filter((m) => m.pending);
                if (pending.length) {
                    setMessages((prev) => prev.filter((m) => !m.pending));
                    const text = pending.map((m) => m.content).filter(Boolean).join('\n');
                    if (text) setInput((prev) => (prev.trim() ? `${text}\n${prev}` : text));
                }
            }
            setIsLoading(false);
            if (refusal.readOnly) {
                setRefusedReadOnly(refusal.message);
                setInputError('');
            } else {
                setInputError(refusal.message);
            }
        };

        socket.on('chatInitialized', handleChatInitialized);
        socket.on('roomSummary', handleRoomSummary);
        socket.on('history', handleHistory);
        socket.on('receiveMessage', handleReceiveMessage);
        socket.on('userTyping', handleUserTyping);
        socket.on('userStoppedTyping', handleUserStoppedTyping);
        socket.on('userStatusUpdate', handleUserStatusUpdate);
        socket.on('messagesSeen', handleMessagesSeen);
        socket.on('messageReaction', handleMessageReaction);
        socket.on('connect', handleReconnect);
        socket.on('error', handleSocketError);

        return () => {
            socket.off('chatInitialized', handleChatInitialized);
            socket.off('roomSummary', handleRoomSummary);
            socket.off('history', handleHistory);
            socket.off('receiveMessage', handleReceiveMessage);
            socket.off('userTyping', handleUserTyping);
            socket.off('userStoppedTyping', handleUserStoppedTyping);
            socket.off('userStatusUpdate', handleUserStatusUpdate);
            socket.off('messagesSeen', handleMessagesSeen);
            socket.off('messageReaction', handleMessageReaction);
            socket.off('connect', handleReconnect);
            socket.off('error', handleSocketError);
        };
    }, [doctor?.doctorId, patientId, socket]);

    useEffect(() => {
        if (atBottomRef.current) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    // Mark messages as seen when viewing the chat
    useEffect(() => {
        if (!roomId || !socket || messages.length === 0) return;
        const timer = setTimeout(() => {
            socket.emit('markAsSeen', { roomId, userId: patientId, role: 'patient' });
        }, 500);
        return () => clearTimeout(timer);
    }, [roomId, socket, patientId, messages.length]);

    const handleScroll = () => {
        if (!chatAreaRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = chatAreaRef.current;
        const nearBottom = scrollHeight - scrollTop - clientHeight < 100;
        atBottomRef.current = nearBottom;
        setShowScrollBottom(!nearBottom);
    };

    const scrollToBottom = () => {
        atBottomRef.current = true;
        setShowScrollBottom(false);
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Tapping a quoted reply jumps to the message it answers.
    const jumpToQuoted = useCallback((targetId) => {
        if (!targetId) return;
        const element = document.getElementById(`message-${targetId}`);
        if (!element) return;
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-[#4ABDBD]/50', 'rounded-2xl');
        setTimeout(() => element.classList.remove('ring-2', 'ring-[#4ABDBD]/50', 'rounded-2xl'), 1600);
    }, []);

    const handlePickFiles = (fileList) => {
        const { files, error } = pickValidChatFiles(fileList, selectedFiles, t);
        if (files.length > 0) setSelectedFiles(prev => [...prev, ...files].slice(0, MAX_CHAT_FILES));
        setInputError(error);
    };

    const handleDownload = useCallback(async (fileUrl, fileName) => {
        try {
            await downloadChatAttachment(fileUrl, fileName);
        } catch (error) {
            console.error('Error downloading file:', error);
            toast.error(t('chat.downloadFailed', 'Could not download the file. Please try again.'));
        }
    }, [t]);

    // Toggle this reader's reaction on one message.
    //
    // Optimistic, then reconciled by the messageReaction broadcast the server
    // sends to the whole room — including back to us, so the success path needs
    // no follow-up here. The ack exists for the FAILURE path only: a rejected
    // reaction (expired token, a message that is not in this room, an emoji the
    // server doesn't allow) gets no broadcast, and without the rollback the chip
    // would sit there for the rest of the session claiming something that was
    // never stored. `previous` is the array the caller rendered from, so the
    // rollback restores exactly what the server still holds.
    const handleReact = useCallback((msg, emoji) => {
        const messageId = serverMessageId(msg?.id || msg?._id);
        if (!messageId || !socket || !roomId) return;

        const previous = Array.isArray(msg.reactions) ? msg.reactions : [];
        const me = { userId: patientId, userRole: 'patient' };
        const optimistic = applyReactionLocally(previous, me, emoji);

        setMessages(prev => prev.map(m => (String(m.id) === messageId ? { ...m, reactions: optimistic } : m)));

        socket.emit('reactToMessage', { roomId, messageId, emoji }, (res) => {
            if (res?.ok) return;
            setMessages(prev => prev.map(m => (String(m.id) === messageId ? { ...m, reactions: previous } : m)));
            setInputError(t('chat.reactionFailed', 'Could not save that reaction. Please try again.'));
        });
    }, [patientId, roomId, socket, t]);

    const handleInputChange = (value) => {
        const sanitized = value.replace(/[<>]/g, '');
        if (sanitized.length > MAX_MESSAGE_LENGTH) {
            setInputError(t('chat.message_too_long', { length: MAX_MESSAGE_LENGTH }));
            return;
        }
        setInputError('');
        setInput(sanitized);

        if (!roomId || !socket) return;
        if (sanitized.trim()) {
            socket.emit('typing', { roomId, userId: patientId, role: 'patient' });
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => {
                socket.emit('stopTyping', { roomId, userId: patientId, role: 'patient' });
            }, 2000);
        } else {
            socket.emit('stopTyping', { roomId, userId: patientId, role: 'patient' });
        }
    };

    // Shared by the attachment send and the voice note. The REST route persists
    // the row and broadcasts it, so nothing is appended locally.
    const uploadToRoom = useCallback(async (files, message, replyId) => {
        const currentRoomId = roomId || `room_${doctor.doctorId}_${patientId}`;
        const formData = new FormData();
        formData.append('roomId', currentRoomId);
        formData.append('senderId', patientId);
        formData.append('senderRole', 'patient');
        if (message) formData.append('message', message);
        formData.append('doctorId', doctor.doctorId);
        formData.append('patientId', patientId);
        if (replyId) formData.append('replyTo', replyId);
        files.forEach(file => formData.append('files', file));

        setIsUploading(true);
        try {
            await axios.post(`${baseURL}/chat/send`, formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            return true;
        } catch (error) {
            console.error('Error sending message with files:', error);
            // 30 Sep 2026: a refusal's `message` can be { en, fr } — handing the
            // object to the error line rendered it as a React child and took the
            // whole chat down. Always reduce it to a string in the UI language.
            setInputError(extractErrorMessage(
                error?.response?.data?.message,
                t('chat.sendFailed', 'Failed to send message'),
                (i18n.language || 'en').slice(0, 2),
            ));
            return false;
        } finally {
            setIsUploading(false);
        }
    }, [doctor?.doctorId, i18n.language, patientId, roomId, t]);

    const handleSend = useCallback(async () => {
        const userMessage = input.trim();
        if ((!userMessage && selectedFiles.length === 0) || isUploading || lockedReason) return;

        const currentRoomId = roomId || `room_${doctor.doctorId}_${patientId}`;
        const replyId = serverMessageId(replyToMessage?.id || replyToMessage?._id);

        if (selectedFiles.length > 0) {
            const ok = await uploadToRoom(selectedFiles, userMessage, replyId);
            if (!ok) return;
            setInput('');
            setSelectedFiles([]);
            setInputError('');
            setReplyToMessage(null);
            return;
        }

        setMessages(prev => [...prev, {
            id: `pending-${Date.now()}`,
            pending: true,
            type: 'user',
            content: userMessage,
            timestamp: new Date(),
            status: 'sending',
            replyToMessage: replyToMessage || null,
            attachments: [],
        }]);
        setInput('');
        setInputError('');
        setReplyToMessage(null);
        atBottomRef.current = true;

        socket.emit('sendMessage', {
            roomId: currentRoomId,
            senderId: patientId,
            senderRole: 'patient',
            message: userMessage,
            doctorId: doctor.doctorId,
            patientId,
            replyTo: replyId,
        });
        socket.emit('stopTyping', { roomId: currentRoomId, userId: patientId, role: 'patient' });
    }, [doctor?.doctorId, input, isUploading, lockedReason, patientId, replyToMessage, roomId, selectedFiles, socket, uploadToRoom]);

    const handleVoiceNote = useCallback(async (file) => {
        if (lockedReason) return;
        const ok = await uploadToRoom([file], '', serverMessageId(replyToMessage?.id || replyToMessage?._id));
        if (ok) {
            setReplyToMessage(null);
            setInputError('');
        } else {
            toast.warning(t('chat.sendFailed', 'Failed to send message'));
        }
    }, [lockedReason, replyToMessage, t, uploadToRoom]);

    const doctorName = doctor?.name || t('chat.doctor', 'Doctor');

    return (
        <div className="flex flex-col h-full bg-white" data-testid="doctor-chat-container">
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10 flex items-center gap-3 flex-shrink-0" data-testid="doctor-chat-header">
                <div className="relative flex-shrink-0" data-testid="doctor-chat-avatar-container">
                    {(doctor?.avatar || doctor?.image) ? (
                        <img
                            src={getFileURL(doctor?.avatar || doctor?.image)}
                            alt={doctor?.name}
                            className="w-10 h-10 rounded-full object-cover border-2 border-white shadow"
                            data-testid="doctor-chat-avatar-image"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#4ebfbf] to-[#3A9B9B] flex items-center justify-center border-2 border-white shadow" data-testid="doctor-chat-avatar-placeholder">
                            <span className="text-white font-semibold" data-testid="doctor-chat-avatar-initial">
                                {doctor?.name?.charAt(0)?.toUpperCase()}
                            </span>
                        </div>
                    )}
                    {isOnline && (
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" data-testid="doctor-chat-online-indicator" />
                    )}
                </div>
                <div className="min-w-0" data-testid="doctor-chat-header-info">
                    <h3 className="font-semibold text-gray-800 truncate" data-testid="doctor-chat-doctor-name">{doctorName}</h3>
                    <p className="text-xs text-gray-500" data-testid="doctor-chat-status-text">
                        {isTyping
                            ? <span className="text-[#2D8A8A] font-medium" data-testid="doctor-chat-header-typing-text">{t('chat.typing', 'Typing...')}</span>
                            : isOnline ? t('chat.online', 'Online') : t('chat.offline', 'Offline')}
                    </p>
                </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 relative min-h-0 flex flex-col" data-testid="doctor-chat-messages-section">
                <div
                    ref={chatAreaRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto custom-scrollbar p-4 bg-gray-50"
                    data-testid="doctor-chat-messages-area"
                >
                    {messages.length === 0 && !isLoading && (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-2" data-testid="doctor-chat-empty-state">
                            <MessageCircle size={44} className="text-gray-300" data-testid="doctor-chat-empty-icon" />
                            <p className="text-sm" data-testid="doctor-chat-empty-text">{t('chat.startConversation', { name: doctor?.name })}</p>
                        </div>
                    )}

                    {messages.map((msg, index) => {
                        // Guarded: formatDateHeader(null) would resolve to the
                        // epoch and stamp "1 January 1970" over the thread.
                        const dateLabel = msg.timestamp ? formatDateHeader(msg.timestamp) : '';
                        const previousLabel = index > 0 && messages[index - 1].timestamp
                            ? formatDateHeader(messages[index - 1].timestamp) : '';
                        return (
                            <ChatMessageBubble
                                key={msg._id || msg.id}
                                msg={msg}
                                myRole="patient"
                                myUserId={patientId}
                                testPrefix="doctor-chat"
                                showDateHeader={dateLabel !== previousLabel}
                                dateLabel={dateLabel}
                                onReply={setReplyToMessage}
                                onReact={handleReact}
                                onOpenImages={(images) => { setPreviewImages(images); setPreviewImageIndex(0); }}
                                onDownload={handleDownload}
                                onJumpToQuoted={jumpToQuoted}
                            />
                        );
                    })}

                    {isLoading && (
                        <div className="flex justify-start" data-testid="doctor-chat-loading-container">
                            <div className="bg-white text-gray-800 border border-gray-200 rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm">
                                <Loader2 className="animate-spin text-[#4ABDBD]" size={18} data-testid="doctor-chat-loading-icon" />
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {showScrollBottom && (
                    <button
                        type="button"
                        onClick={scrollToBottom}
                        className="absolute bottom-4 right-6 p-3 bg-[#4ABDBD] text-white rounded-full shadow-lg hover:bg-[#3A9B9B] transition-all z-10 cursor-pointer"
                        title={t('chat.recent_messages', 'Recent messages')}
                        aria-label={t('chat.recent_messages', 'Recent messages')}
                        data-testid="doctor-chat-scroll-bottom-button"
                    >
                        <ChevronDown size={18} data-testid="doctor-chat-scroll-bottom-icon" />
                    </button>
                )}
            </div>

            <ChatComposer
                value={input}
                onChange={handleInputChange}
                onSend={handleSend}
                onInsertEmoji={(emoji) => handleInputChange(input + emoji)}
                error={inputError}
                maxLength={MAX_MESSAGE_LENGTH}
                disabled={!roomId}
                placeholder={t('chat.typeMessage', 'Type your message...')}
                peerName={doctorName}
                peerTyping={isTyping}
                replyTo={replyToMessage}
                replyToLabel={replyToMessage?.type === 'user' ? t('chat.you', 'You') : doctorName}
                onCancelReply={() => setReplyToMessage(null)}
                files={selectedFiles}
                onPickFiles={handlePickFiles}
                onRemoveFile={(index) => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                isUploading={isUploading}
                onVoiceNote={handleVoiceNote}
                testPrefix="doctor-chat"
                lockedReason={lockedReason}
            />

            <ChatImageLightbox
                images={previewImages}
                index={previewImageIndex}
                onIndexChange={setPreviewImageIndex}
                onClose={() => setPreviewImages([])}
                onDownload={handleDownload}
                testPrefix="doctor-chat"
            />
        </div>
    );
};

export default DoctorChatInterface;
